import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, useSearchParams, Navigate } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { Suspense, lazy } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppProvider } from './contexts/AppContext';
import Layout from './components/Layout';
import AppLayout from './components/AppLayout';
import SimpleLayout from './components/SimpleLayout';
import GlobalErrorFallback from './components/common/GlobalErrorFallback';
import PageLoadingFallback from './components/common/PageLoadingFallback';
import { logErrorToService, errorBoundaryResetKeys } from './utils/errorHandling';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AppAuthorized from './shared/auth/AppAuthorized';
import AppSelector from './apps/app-selector/AppSelector';

// Lazy load page components for better performance
const OpportunitiesPage = lazy(() => import('./components/pages/OpportunitiesPage'));
const AdminPage = lazy(() => import('./components/pages/AdminPage'));
const ProfilePage = lazy(() => import('./components/pages/ProfilePage'));
const InvitationAcceptPage = lazy(() => import('./components/pages/InvitationAcceptPage'));
const AccountSettingsPage = lazy(() => import('./components/pages/AccountSettingsPage'));
const ResetPasswordPage = lazy(() => import('./components/pages/ResetPasswordPage'));

// Lazy load app modules
const ContractIntelligenceApp = lazy(() => import('./apps/contract-intelligence/ContractIntelligenceApp'));
const SbirSttrApp = lazy(() => import('./apps/sbir-sttr/SbirSttrApp'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: 1,
      staleTime: 1 * 60 * 1000, // 1 minute default
    },
  },
});

// Component wrapper that conditionally applies layout and handles loading
const PageWrapper = ({ children, layoutType = 'default' }) => {
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embed') === 'true';

  const content = (
    <Suspense fallback={<PageLoadingFallback />}>
      {children}
    </Suspense>
  );

  if (isEmbedded) {
    return <div className="min-h-screen bg-gray-50 dark:bg-gray-900">{content}</div>;
  }

  // Choose layout based on type
  switch (layoutType) {
    case 'app':
      return <AppLayout>{content}</AppLayout>;
    case 'simple':
      return <SimpleLayout>{content}</SimpleLayout>;
    default:
      return <Layout>{content}</Layout>;
  }
};

// Component to handle home route based on authentication
const HomeRoute = () => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  // If user is authenticated, redirect to app selector
  if (user) {
    return <Navigate to="/app-select" replace />;
  }
  
  // If not authenticated, show opportunities page
  return <OpportunitiesPage />;
};

// Component to handle routing
const AppContent = () => {
  return (
    <Routes>
      {/* Public Routes - No layout */}
      <Route path="/" element={
        <PageWrapper>
          <HomeRoute />
        </PageWrapper>
      } />
      <Route path="/opportunities" element={
        <PageWrapper>
          <OpportunitiesPage />
        </PageWrapper>
      } />
      <Route path="/invitation" element={
        <PageWrapper>
          <InvitationAcceptPage />
        </PageWrapper>
      } />
      <Route path="/reset-password" element={
        <PageWrapper>
          <ResetPasswordPage />
        </PageWrapper>
      } />
      
      {/* App Selection - Simple layout (no app nav) */}
      <Route path="/app-select" element={
        <PageWrapper layoutType="simple">
          <ProtectedRoute>
            <AppSelector />
          </ProtectedRoute>
        </PageWrapper>
      } />
      
      {/* Contract Intelligence App Routes - App layout */}
      <Route path="/app/contracts/*" element={
        <PageWrapper layoutType="app">
          <ProtectedRoute>
            <AppAuthorized app="contract-intelligence">
              <ContractIntelligenceApp />
            </AppAuthorized>
          </ProtectedRoute>
        </PageWrapper>
      } />
      
      {/* SBIR/STTR App Routes - App layout */}
      <Route path="/app/sbir/*" element={
        <PageWrapper layoutType="app">
          <ProtectedRoute>
            <AppAuthorized app="sbir-sttr">
              <SbirSttrApp />
            </AppAuthorized>
          </ProtectedRoute>
        </PageWrapper>
      } />
      
      {/* Shared Routes - Simple layout */}
      <Route path="/profile" element={
        <PageWrapper layoutType="simple">
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        </PageWrapper>
      } />
      <Route path="/account-settings" element={
        <PageWrapper layoutType="simple">
          <ProtectedRoute>
            <AccountSettingsPage />
          </ProtectedRoute>
        </PageWrapper>
      } />
      
      {/* Admin Routes - Simple layout */}
      <Route path="/admin" element={
        <PageWrapper layoutType="simple">
          <ProtectedRoute requireAdmin={true}>
            <AdminPage />
          </ProtectedRoute>
        </PageWrapper>
      } />
      
      {/* Legacy Routes - Redirect to new structure */}
      <Route path="/dashboard" element={<Navigate to="/app/contracts" replace />} />
      <Route path="/ai-assistant" element={<Navigate to="/app/contracts/ai-assistant" replace />} />
      <Route path="/competitive-analysis" element={<Navigate to="/app/contracts/competitive-analysis" replace />} />
      <Route path="/contract-intelligence" element={<Navigate to="/app/contracts" replace />} />
      <Route path="/opportunity-intelligence/:slug" element={<Navigate to="/app/contracts/opportunity/:slug" replace />} />
      <Route path="/pipelines" element={<Navigate to="/app/contracts/pipelines" replace />} />
      <Route path="/pipeline" element={<Navigate to="/app/contracts/pipelines" replace />} />
      <Route path="/pipeline/:slug" element={<Navigate to="/app/contracts/pipeline/:slug" replace />} />
    </Routes>
  );
};

function App() {
  const handleError = (error, errorInfo) => {
    logErrorToService({
      error: error.toString(),
      errorInfo,
      context: 'Global App Error'
    });
  };

  return (
    <ErrorBoundary
      FallbackComponent={GlobalErrorFallback}
      onError={handleError}
      onReset={() => {
        // Clear any app-level state if needed
        window.location.reload();
      }}
      resetKeys={[errorBoundaryResetKeys.ROUTE_CHANGE]}
    >
      <QueryClientProvider client={queryClient}>
        <Router>
          <ThemeProvider>
            <AuthProvider>
              <AppProvider>
                <AppContent />
              </AppProvider>
            </AuthProvider>
          </ThemeProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;